import React, {
	ChangeEvent,
	useState,
	useReducer,
	FormEvent,
	useEffect,
} from 'react';
import { text, unitList } from '../data';
import PartSearch from '../../components/bearing-search/components/PartSearch';
import {
	BearingCalculationData,
	BearingInfo,
	BearingTolerancesPayload,
	BearingTypeCode,
	FittingPracticeCalculationPayload,
	TimkenUnit,
} from '../../components/bearing-search/types/types';
import {
	getBearingClasses,
	getBearingTolerances,
	taperedBoreMessage,
} from '../../components/bearing-search/api/api';
import { SelectOption } from '../types/types';
import CalculationResults from '../components/CalculationResults';
import tolerancesReducer, { initialTolerancesState } from './reducer';
import partReducer, {
	BearingPartState,
	initialBearingPartState,
} from '../../components/bearing-search/reducers/reducer';
import {
	getDimensionLabel,
	getTooltip,
	toggleImportantNote,
	toggleNextTools,
	updateToolParams,
	getPartNumberFromUrl,
	toggleAdditionalNote,
	getPartStateFromParams,
	toggleReportReady,
} from '../utils/engineering-tools';
import TextInput from '../../components/bearing-search/components/forms/TextInput';
import SelectDropdown from '../../components/bearing-search/components/forms/SelectDropdown';
import {
	trackStart,
	trackResults,
	updateActionParams,
} from '../utils/analytics';
import PartPreSearch from '../components/PartPreSearch';
import DOMPurify from "dompurify";

const BearingTolerances: React.FC = () => {
	const [ isPartSelected, setIsPartSelected ] = useState<boolean>(null);
	const [ isPartOpen, setIsPartOpen ] = useState( false );
	const [ isCalculated, setIsCalculated ] = useState( false );
	const [ isBearingClassesLoading, setIsBearingClassesLoading ] = useState( false );
	const [ isResultsLoading, setIsResultsLoading ] = useState( false );
	const [ searchInput, setSearchInput ] = useState( '' );
	const [ results, setResults ] = useState<Array<BearingCalculationData | null>>(
		[],
	);
	const [ dimensionLabel, setDimensionLabel ] = useState( '' );
	const [ bearingClasses, setBearingClasses ] = useState<SelectOption<string>[]>(
		[],
	);
	const [ partState, partDispatch ] = useReducer(
		partReducer,
		initialBearingPartState,
	);
	const [ formState, dispatch ] = useReducer(
		tolerancesReducer,
		initialTolerancesState,
	);

	useEffect( () => {
		trackStart( 'bearingTolerances' );

		const queryMap = new URLSearchParams( document.location.search );
		const savedPart = getPartStateFromParams( queryMap );

		if (queryMap.size == 1) {
			const partNumberFromUrl = getPartNumberFromUrl();
			if (partNumberFromUrl) {
				setSearchInput(partNumberFromUrl);
				partDispatch({
					type: 'select_part',
					payload: { marketPlaceDescription: partNumberFromUrl },
				});
				setIsPartSelected( true );
				handleSubmit();
			}
		} else if (queryMap.size > 1) {
			if ( savedPart.partNumber ) {
				partDispatch( {
					type: 'reset',
					payload: savedPart,
				} );
				setIsPartSelected( true );
				setSearchInput( savedPart.marketPlaceDescription || '' );
			}
		}
	}, [] );

	// Show / hide recommended tools
	useEffect( () => {
		const isShown = isCalculated && ! isResultsLoading;
		toggleNextTools( isShown );
		toggleReportReady(
			isShown,
			partState.marketPlaceDescription,
			partState.primaryImage,
		);

		return () => toggleNextTools( false );
	}, [ isCalculated, isResultsLoading ] );

	// Pass partNumber and bearingClass to recommended tools link
	useEffect( () => {
		const { bearingClass } = formState;
		type ToolQueries = BearingPartState &
			Pick<FittingPracticeCalculationPayload, 'bearingClass'>;

		updateToolParams<ToolQueries>( {
			...partState,
			bearingClass,
		} );

		return () => updateToolParams();
	}, [ results ] );

	useEffect( () => {
		if ( partState.bearingType ) {
			setIsBearingClassesLoading( true );

			getBearingClasses( {
				bearingType: partState.bearingType,
				designUnit:
					partState.bearingType === BearingTypeCode.TRB
						? partState.designUnit
						: undefined,
			} )
				.then( ( response ) => {
					const options = response.bearingClassList.map( ( item ) => {
						return {
							id: item.value,
							value: item.value,
							label: item.displayLabel,
						} as SelectOption<string>;
					} );

					if ( options.length > 0 ) {
						setBearingClasses( options );

						dispatch( {
							type: 'change_bearing_class',
							payload: options[ 0 ]?.value,
						} );
					}
				} )
				.finally( () => {
					setIsBearingClassesLoading( false );
				} );
		}
	}, [ partState.bearingType ] );

	const inputPartNumber = ( event: ChangeEvent<HTMLInputElement> ) => {
		const value = event.target.value;
		setSearchInput( value );
		setIsPartSelected( false );
	};

	const verifyPart = ( part?: BearingInfo ) => {
		if ( part ) {
			partDispatch( {
				type: 'select_part',
				payload: part,
			} );
			setIsPartSelected( true );
			setSearchInput( part.marketPlaceDescription || '' );
		}

		setIsPartOpen( false );
	};

	const clearSearch = () => {
		setSearchInput( '' );
		setIsCalculated( false );
		setIsPartSelected( false );
		toggleImportantNote( false );
		toggleAdditionalNote( false );

		partDispatch( {
			type: 'reset',
		} );

		dispatch( {
			type: 'reset',
		} );
	};

	const handleSubmit = ( event?: FormEvent ) => {
		if (event) {
			event.preventDefault();
		}

		if ( ! partState.partNumber ) {
			// Search Part
			setIsPartOpen( true );
		} else {
			// Calculate Tolerances
			setIsCalculated( true );
			setIsResultsLoading( true );
			toggleImportantNote( false );
			toggleAdditionalNote( false );

			const { partNumber, cupPartNumber, bearingType, bearingSubType } =
				partState;

			const request: Partial<BearingTolerancesPayload> = {
				partNumber,
				cupPartNumber,
				bearingType,
				bearingSubType,
				...formState,
			};

			getBearingTolerances( request )
				.then( ( response ) => {
					updateActionParams( 'bearingTolerances', partState );
					let reportData: Array<BearingCalculationData | null> = [];

					if ( response.report && response.report.length > 0 ) {
						reportData = response.report[ 0 ].data;
					}

					setResults( reportData );
					trackResults( 'bearingTolerances', request, reportData.length, partState.marketPlaceDescription );
					setDimensionLabel( getDimensionLabel( partState, formState.unit ) );
					toggleImportantNote( true );
					toggleAdditionalNote( true );

					document.querySelector('.t-engineering-tools__results').scrollIntoView({
						behavior: 'smooth',
					});
				} )
				.finally( () => setIsResultsLoading( false ) );
		}
	};

	const preSearchClickHandler = () => {
		setIsPartSelected(true);
	};

	const setPartFromPreSearchAndSubmit = (value: string, e: React.MouseEvent<HTMLElement>) => {
		setSearchInput( value );
		handleSubmit(e);
	};

	return (
		<>
			<div>
				<form onSubmit={handleSubmit}>
					<div className="t-engineering-tools__form-fields t-engineering-tools__form-fields--multi-column timken-tool-presearch-container">
						<TextInput
							id="bearing_tolerance_part_number"
							required={true}
							label={text.partNumber}
							verified={!! partState.partNumber}
							name="partNumber"
							value={searchInput}
							disabled={!! partState.partNumber}
							placeholder={text.partNumber}
							message={
								partState.partNumber
									? `${ text.bearingType }: ${ partState.bearingTypeLabel || partState.typeDescription }`
									: text.partWildCardHint
							}
							onChange={inputPartNumber}
						/>
						<PartPreSearch
							input={searchInput}
							isHidden={searchInput.length < 2 || isPartOpen || isPartSelected}
							unit={formState.unit}
							onClick={preSearchClickHandler}
							verifyPartNumber={(value: string, e) => setPartFromPreSearchAndSubmit(value, e)}
						/>

						<SelectDropdown
							id="bearing_tolerance_unit"
							name="unit"
							required={true}
							label={text.displayUnits}
							value={formState.unit}
							options={unitList}
							onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
								dispatch( {
									type: 'change_unit',
									payload: event.target.value as TimkenUnit,
								} )
							}
						/>

						{! partState.partNumber && (
							<button
								className="btn btn--primary"
								type="submit"
								disabled={! searchInput}
							>
								{text.lookup}
							</button>
						)}

						{!! partState.partNumber && (
							<>
								<SelectDropdown
									id="bearing_tolerance_class"
									name="bearingClass"
									required={true}
									label={text.bearingClass}
									tooltip={getTooltip( text.bearingClass )}
									value={formState.bearingClass}
									options={bearingClasses}
									loading={isBearingClassesLoading}
									onChange={( event: ChangeEvent<HTMLSelectElement> ) =>
										dispatch( {
											type: 'change_bearing_class',
											payload: event.target.value,
										} )
									}
								/>
							</>
						)}
					</div>

					{partState.taperedBore === 'Y' && (
						<div className="t-engineering-tools__tapered-bore-message"
							 dangerouslySetInnerHTML={{__html: DOMPurify.sanitize(taperedBoreMessage)}}
						/>
					)}

					{!! partState.partNumber && (
						<div>
							{partState.taperedBore === 'N' && (
								<>
									<div className="t-engineering-tools__terms-of-use"
										 dangerouslySetInnerHTML={{ __html: text.calculateTermsOfUse }}
									/>
								</>
							)}
							<div className="t-engineering-tools__search-actions">
								{partState.taperedBore === 'N' && (
									<button
										className="btn btn--primary"
										type="submit"
										disabled={! formState.bearingClass || isResultsLoading}
									>
										{text.calculate}
									</button>
								)}

								<button
									className="btn btn--secondary"
									type="button"
									onClick={() => clearSearch()}
								>
									{text.clear}
								</button>
							</div>
						</div>
					)}
				</form>
			</div>

			{isCalculated && (
				<CalculationResults
					data={results}
					label={dimensionLabel}
					loading={isResultsLoading}
				/>
			)}

			{isPartOpen && !! searchInput && (
				<PartSearch
					units={formState.unit}
					partNumber={searchInput}
					onSelect={verifyPart}
					onClose={() => setIsPartOpen( false )}
				/>
			)}
		</>
	);
};

export default BearingTolerances;
